/**
 * GovEarner Organic Content: nav gate, pairing-distance labels, draft save.
 * Default operator copy never uses cosine jargon. Pairing bands stay on Pack.
 */

import { DollarSign, PenLine } from 'lucide-react';
import { getOrganicDeskSettings, ORGANIC_DESK_SETTINGS_PATH, type SuggestedPackId } from './organicDeskSettings';

export const SPARC_TENANT_ID = '52fb10b9-043c-43a9-bbbb-a7d56258a192';
export const ORGANIC_CONTENT_PATH = '/rfp/organic-content';
export const COST_ANALYSIS_PATH = '/rfp/cost-analysis';
export { ORGANIC_DESK_SETTINGS_PATH };
export const DATA_PIPELINE_ORDER = 5;
export const EDITORIAL_BOARD_SLOTS = 3;
export { SUGGESTED_PACK_IDS, isSuggestedPackId } from './organicDeskSettings';
export type { SuggestedPackId } from './organicDeskSettings';
export const ORGANIC_GENERATE_ENABLED = true;
export const ORGANIC_DRAFT_STORAGE_PREFIX = 'sparc-organic-draft-v1';

export type Channel = 'beehiiv' | 'insights' | 'linkedin' | 'x';
export type PreviewSurface = Channel | 'still';
export type CosineBand = 'orthogonal' | 'middle' | 'cone';

export const CHANNELS: Channel[] = ['beehiiv', 'insights', 'linkedin', 'x'];
export const PREVIEW_SURFACES: PreviewSurface[] = ['x', 'linkedin', 'insights', 'beehiiv', 'still'];

export const ORGANIC_WORKSPACE_TABS = [
  { id: 'pack', label: 'Pack' },
  { id: 'pairing', label: 'Pairing' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'drafts', label: 'Drafts' },
  { id: 'keepers', label: 'Keepers' },
] as const;

export type OrganicWorkspaceTabId = (typeof ORGANIC_WORKSPACE_TABS)[number]['id'];

export const ORGANIC_WORKSPACE_TAB_KEY = 'sparc-organic-workspace-tab';

export function isOrganicWorkspaceTab(value: string): value is OrganicWorkspaceTabId {
  return ORGANIC_WORKSPACE_TABS.some((tab) => tab.id === value);
}

export function readOrganicWorkspaceTab(): OrganicWorkspaceTabId {
  if (typeof sessionStorage === 'undefined') return 'pack';
  try {
    const raw = sessionStorage.getItem(ORGANIC_WORKSPACE_TAB_KEY) ?? '';
    return isOrganicWorkspaceTab(raw) ? raw : 'pack';
  } catch {
    return 'pack';
  }
}

export function writeOrganicWorkspaceTab(tab: OrganicWorkspaceTabId): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(ORGANIC_WORKSPACE_TAB_KEY, tab);
  } catch {
    /* ignore quota */
  }
}

export const ABLATION_ARM_IDS = [
  'A_orth',
  'A_mid',
  'A_cone',
  'A_all',
  'A_orth_plus_mid',
  'A_floor_016',
  'A_floor_020',
  'A_floor_028',
] as const;

/** Pairing sets the desk uses. Floor arms stay in Analyst Detail. */
export const OPERATOR_PAIRING_ARM_IDS = [
  'A_orth',
  'A_mid',
  'A_cone',
  'A_orth_plus_mid',
] as const;

export function isOperatorPairingArm(id: string): boolean {
  return (OPERATOR_PAIRING_ARM_IDS as readonly string[]).includes(id);
}

export const EQUAL_ABLATION_WEIGHTS = {
  virality: 0.25,
  relevance: 0.25,
  nuance: 0.25,
  contrarian: 0.25,
} as const;

export type AblationWeights = {
  virality: number;
  relevance: number;
  nuance: number;
  contrarian: number;
};

const WEIGHT_KEYS: Array<keyof AblationWeights> = [
  'virality',
  'relevance',
  'nuance',
  'contrarian',
];

export function clampWeight(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

export function weightedAblationTotal(
  scores: Pick<AblationArm, 'virality' | 'relevance' | 'nuance' | 'contrarian'>,
  weights: AblationWeights,
): number | null {
  let sum = 0;
  let used = false;
  for (const key of WEIGHT_KEYS) {
    const score = scores[key];
    if (score == null) continue;
    sum += score * clampWeight(weights[key]);
    used = true;
  }
  return used ? sum : null;
}

export const BAND_COPY: Record<CosineBand, { label: string; sentence: string }> = {
  cone: {
    label: 'Close to the story as written',
    sentence: 'These pairings stay close to the story as written.',
  },
  middle: {
    label: 'Same industry, different angle',
    sentence: 'These pairings stay in the same industry but take a different cut.',
  },
  orthogonal: {
    label: 'Surprising but still in-topic',
    sentence: 'These pairings hit a document from another vertical.',
  },
};

/** Operator chrome: short legend. Long BAND_COPY labels stay in Analyst detail. */
export const OPERATOR_BAND_COPY: Record<CosineBand, { label: string; sentence: string }> = {
  cone: {
    label: 'Close',
    sentence: 'Sits next to the story as written.',
  },
  middle: {
    label: 'Related',
    sentence: 'Same cluster, a different cut.',
  },
  orthogonal: {
    label: 'Surprising',
    sentence: 'Hits a document from another vertical.',
  },
};

export const SCORE_COPY = {
  virality: 'Hook',
  relevance: 'On the news',
  nuance: 'Not a slogan',
  contrarian: 'Unexpected take',
} as const;

export const CHANNEL_LIMITS = {
  get x() {
    return getOrganicDeskSettings().channelLimits.x;
  },
  get linkedin() {
    return getOrganicDeskSettings().channelLimits.linkedin;
  },
  get insightsTitle() {
    return getOrganicDeskSettings().channelLimits.insightsTitle;
  },
  get insightsDek() {
    return getOrganicDeskSettings().channelLimits.insightsDek;
  },
  get beehiivHeadline() {
    return getOrganicDeskSettings().channelLimits.beehiivHeadline;
  },
};

export const STILL_CAPTION = 'SPARC still. Not a news photo.';

const STILL_BAN_UI =
  'Not a software dashboard. Not a product screenshot. No UI chrome, no menus, no legends drawn on the frame, no ovals, no circles, no colour wash over an application window.';

const EO_CLUSTER_IDS = new Set(['geoai', 'agri', 'forest', 'mine', 'defence']);

const VERTICAL_MODALITY_LABEL: Record<string, string> = {
  agri: 'NDVI',
  forest: 'NDVI',
  mine: 'SAR',
  defence: 'SAR',
  geoai: 'optical',
  maritime: 'SAR',
  energy: 'thermal',
  environment: 'NDVI',
  land: 'false-color',
  govtech: 'false-color',
  infrastructure: 'false-color',
};

function clusterKeyFromVertical(vertical?: string): string {
  return (vertical || '').trim().toLowerCase();
}

function stillModalityFromStory(title: string, dek: string, cluster: string): string {
  const blob = `${title} ${dek}`.toLowerCase();
  if (/flood|inundat|overflow/.test(blob)) return 'NDVI flood extent';
  if (/lease|concession|claim/.test(blob)) return 'optical lease boundary';
  if (/canopy|deforest|timber/.test(blob)) return 'NDVI canopy';
  if (/sar|radar|subsidence/.test(blob)) return 'SAR';
  return VERTICAL_MODALITY_LABEL[cluster] ?? 'optical';
}

function documentarySceneFromStory(title: string, dek: string, cluster: string): string {
  const blob = `${title} ${dek}`.toLowerCase();
  if (/hearing|committee|parliament|testimony/.test(blob)) {
    return 'a legislative hearing room with papers on the bench';
  }
  if (/port|harbour|harbor|customs|shipping/.test(blob)) {
    return 'a working port office with stacked paperwork';
  }
  if (/factory|plant|mill|smelter/.test(blob)) {
    return 'a factory floor with workers at a distance, no logos';
  }
  if (cluster === 'procurement') return 'a government procurement desk with stamped folders';
  if (cluster === 'sovereign') return 'a customs or ministry counter with ledgers';
  return 'an ESG audit table with printed reports and a window onto a worksite';
}

const DEFAULT_JARGON = [
  /\bA_orth\b/i,
  /\bA_mid\b/i,
  /\bA_cone\b/i,
  /\bA_all\b/i,
  /\bA_orth_plus_mid\b/i,
  /\bA_floor_0\d+\b/i,
  /\bcosine\b/i,
  /\bOrthogonal\b/,
  /\bCone\b/,
  /\bMiddle\b/,
  /\bsame beat\b/i,
  /\bdelve\b/i,
  /\brobust\b/i,
  /\bseamless\b/i,
  /\btapestry\b/i,
  /\bshowcase\b/i,
];

export function isSparcTenant(tenantId: string | null | undefined): boolean {
  return !tenantId || tenantId === SPARC_TENANT_ID;
}

export function costingPlatformNav(enabled: boolean) {
  if (!enabled) return [];
  return [
    {
      label: 'Cost Analysis',
      icon: DollarSign,
      path: COST_ANALYSIS_PATH,
      section: 'RFP Agent',
      description: 'GovEarner platform run-rate and bespoke costing',
      order: 3.5,
    },
    {
      label: 'Organic Content',
      icon: PenLine,
      path: ORGANIC_CONTENT_PATH,
      section: 'RFP Agent',
      description: 'Aperture, Insights, LinkedIn, and X drafts',
      order: 4.5,
    },
  ];
}

export function organicNavSitsBetweenCostAndPipeline(
  items: Array<{ path: string; order?: number }>,
): boolean {
  const cost = items.find((item) => item.path === COST_ANALYSIS_PATH);
  const organic = items.find((item) => item.path === ORGANIC_CONTENT_PATH);
  if (!cost || !organic) return false;
  const costOrder = cost.order ?? 0;
  const organicOrder = organic.order ?? 0;
  return costOrder < organicOrder && organicOrder < DATA_PIPELINE_ORDER;
}

export function cosineBand(cosine: number): CosineBand {
  if (cosine <= 0.2) return 'orthogonal';
  if (cosine >= 0.6) return 'cone';
  return 'middle';
}

export function cosineValue(raw: number | string | null | undefined): number {
  const value = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** Packer human_band → CosineBand. far/related/close never appear in operator chrome. */
export const PACKER_HUMAN_BAND: Record<string, CosineBand> = {
  far: 'orthogonal',
  related: 'middle',
  close: 'cone',
};

export function parseBand(raw: unknown, cosine?: number): CosineBand {
  if (typeof raw === 'string') {
    const key = raw.trim().toLowerCase();
    const fromPacker = PACKER_HUMAN_BAND[key];
    if (fromPacker) return fromPacker;
    if (
      key === 'orth' ||
      key === 'orthogonal' ||
      key.includes('surpris') ||
      key.includes('in-topic')
    ) {
      return 'orthogonal';
    }
    if (key === 'mid' || key === 'middle' || key.includes('industry')) {
      return 'middle';
    }
    if (key === 'cone' || key.includes('close to the story') || key.includes('same beat')) {
      return 'cone';
    }
  }
  if (typeof cosine === 'number' && Number.isFinite(cosine)) {
    return cosineBand(cosine);
  }
  return 'middle';
}

export type PairingBandRow = {
  human_band?: unknown;
  band?: unknown;
  cosine?: number | string | null;
};

export function bandFromPairingRow(row: PairingBandRow): CosineBand {
  return parseBand(row.human_band ?? row.band, cosineValue(row.cosine));
}

export function bandLabel(band: CosineBand): string {
  return BAND_COPY[band].label;
}

export function bandSentence(band: CosineBand): string {
  return BAND_COPY[band].sentence;
}

export function operatorBandLabel(band: CosineBand): string {
  return OPERATOR_BAND_COPY[band].label;
}

export function operatorBandSentence(band: CosineBand): string {
  return OPERATOR_BAND_COPY[band].sentence;
}

export const ANALYST_BAND_LABELS: readonly string[] = [
  BAND_COPY.cone.label,
  BAND_COPY.middle.label,
  BAND_COPY.orthogonal.label,
];

export function operatorChromeHasAnalystBandLabel(text: string): boolean {
  return ANALYST_BAND_LABELS.some((label) => text.includes(label));
}

export type PairingSetCopy = {
  title: string;
  sentence: string;
};

export function pairingSetCopy(id: string): PairingSetCopy {
  switch (id) {
    case 'A_orth':
      return {
        title: 'Surprising',
        sentence: 'Keeps pairings that hit a document from another vertical.',
      };
    case 'A_mid':
      return {
        title: 'Related',
        sentence: 'Keeps pairings in the same industry that take a different cut.',
      };
    case 'A_cone':
      return {
        title: 'Close',
        sentence: 'Keeps pairings that stay close to the story as written.',
      };
    case 'A_all':
      return {
        title: 'Every pairing',
        sentence: 'Scores every pairing. No distance filter.',
      };
    case 'A_orth_plus_mid':
      return {
        title: 'Surprise plus related',
        sentence: 'Keeps surprising and related pairings. Skips close pairings.',
      };
    case 'A_floor_016':
      return {
        title: 'Keep more stories',
        sentence: 'A looser cut. More of today\'s stories stay in the pack.',
      };
    case 'A_floor_020':
      return {
        title: 'Usual cut',
        sentence: 'The usual bar for which of today\'s stories stay in the pack.',
      };
    case 'A_floor_028':
      return {
        title: 'Keep only the strongest',
        sentence: 'A stricter cut. Fewer of today\'s stories stay in the pack.',
      };
    default:
      return {
        title: id.replace(/^A_/, '').replace(/_/g, ' '),
        sentence: 'A pairing set from today\'s pack.',
      };
  }
}

export function suggestedPackCopy(id: SuggestedPackId): PairingSetCopy {
  switch (id) {
    case 'editorial':
      return {
        title: 'Editorial',
        sentence: 'Maximizes variety across the eight verticals.',
      };
    case 'close':
      return {
        title: 'Close-led',
        sentence: 'Prefers stories whose pairings stay Close to SPARC documents.',
      };
    case 'surprising':
      return {
        title: 'Surprising-led',
        sentence: 'Prefers stories that hit a document from another vertical.',
      };
  }
}

export function suggestedPackArmId(id: SuggestedPackId): string | null {
  if (id === 'close') return 'A_cone';
  if (id === 'surprising') return 'A_orth';
  return null;
}

export const PAIR_COUNT_SCORE_WEIGHT = 0.35;

export function suggestedPackStoryScore(
  pack: SuggestedPackId,
  eligibility: number,
  bands: { cone: number; orthogonal: number; middle: number },
  pairCount = 0,
): number {
  const consensus = PAIR_COUNT_SCORE_WEIGHT * Math.log(1 + Math.max(0, pairCount));
  switch (pack) {
    case 'editorial':
      return eligibility + consensus;
    case 'close':
      return bands.cone * 10 + eligibility + consensus;
    case 'surprising':
      return bands.orthogonal * 10 + eligibility + consensus;
  }
}

export type PairingGraphPair = {
  label: string;
  cosine?: number | string | null;
  band?: unknown;
  human_band?: unknown;
};

export type PairingGraphLayoutNode = {
  id: string;
  label: string;
  kind: 'seed' | 'pair' | 'story' | 'asset';
  x: number;
  y: number;
  cosine: number;
  band: CosineBand;
  opacity: number;
};

export type PairingGraphLayoutEdge = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  cosine: number;
  opacity: number;
  band: CosineBand;
  fromId?: string;
  toId?: string;
};

export const PAIRING_GRAPH_MAX_PAIRS = 36;
export const PAIRING_GRAPH_WIDTH = 640;
export const PAIRING_GRAPH_HEIGHT = 360;

function clipLabel(label: string, max = 28): string {
  const trimmed = label.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

export function layoutPairingGraph(
  seedLabel: string,
  pairs: PairingGraphPair[],
  width = PAIRING_GRAPH_WIDTH,
  height = PAIRING_GRAPH_HEIGHT,
): { nodes: PairingGraphLayoutNode[]; edges: PairingGraphLayoutEdge[] } {
  const cx = width / 2;
  const cy = height / 2;
  const inner = Math.min(width, height) * 0.16;
  const outer = Math.min(width, height) * 0.42;
  const seedNode: PairingGraphLayoutNode = {
    id: 'seed',
    label: clipLabel(seedLabel || 'Story', 36),
    kind: 'seed',
    x: cx,
    y: cy,
    cosine: 1,
    band: 'cone',
    opacity: 1,
  };
  const rows = pairs.slice(0, PAIRING_GRAPH_MAX_PAIRS);
  const count = Math.max(rows.length, 1);
  const pairNodes: PairingGraphLayoutNode[] = rows.map((row, index) => {
    const cosine = cosineValue(row.cosine);
    const band = bandFromPairingRow(row);
    const radius = inner + (1 - cosine) * (outer - inner);
    const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
    return {
      id: `pair-${index}-${row.label}`,
      label: clipLabel(row.label),
      kind: 'pair',
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
      cosine,
      band,
      opacity: 0.35 + cosine * 0.65,
    };
  });
  const edges: PairingGraphLayoutEdge[] = pairNodes.map((node) => ({
    x1: cx,
    y1: cy,
    x2: node.x,
    y2: node.y,
    cosine: node.cosine,
    opacity: node.opacity,
    band: node.band,
  }));
  return { nodes: [seedNode, ...pairNodes], edges };
}

export function defaultViewHasJargon(text: string): boolean {
  return DEFAULT_JARGON.some((pattern) => pattern.test(text));
}

export type RankBRow = {
  kept_orthogonal_0_0_0_2?: number;
  dropped_cone_0_6_1_0?: number;
  dropped_middle_0_2_0_6?: number;
  orthogonal?: number;
  middle?: number;
  cone?: number;
  orth?: number;
  mid?: number;
  surprising?: number;
  same_industry?: number;
  same_beat?: number;
  far?: number;
  related?: number;
  close?: number;
};

export type BandHistogram = {
  orthogonal: number;
  middle: number;
  cone: number;
};

function asCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function bandCount(row: RankBRow, band: CosineBand): number {
  switch (band) {
    case 'orthogonal':
      return asCount(
        row.orthogonal ??
          row.orth ??
          row.far ??
          row.surprising ??
          row.kept_orthogonal_0_0_0_2,
      );
    case 'middle':
      return asCount(
        row.middle ?? row.mid ?? row.related ?? row.same_industry ?? row.dropped_middle_0_2_0_6,
      );
    case 'cone':
      return asCount(row.cone ?? row.same_beat ?? row.close ?? row.dropped_cone_0_6_1_0);
    default: {
      const _exhaustive: never = band;
      return _exhaustive;
    }
  }
}

function histogramFromRows(rows: RankBRow[] | undefined): BandHistogram {
  const empty: BandHistogram = { orthogonal: 0, middle: 0, cone: 0 };
  if (!rows?.length) return empty;
  return rows.reduce(
    (acc, row) => ({
      orthogonal: acc.orthogonal + bandCount(row, 'orthogonal'),
      middle: acc.middle + bandCount(row, 'middle'),
      cone: acc.cone + bandCount(row, 'cone'),
    }),
    empty,
  );
}

export function rankBHistogram(
  rows: RankBRow[] | undefined,
  pack?: {
    ablation?: { seeds?: Array<{ bands?: RankBRow }> };
    stats?: { per_seed_rank_b?: RankBRow[] };
  },
): BandHistogram {
  const fromRows = histogramFromRows(rows ?? pack?.stats?.per_seed_rank_b);
  if (fromRows.orthogonal + fromRows.middle + fromRows.cone > 0) {
    return fromRows;
  }
  const seedBands = pack?.ablation?.seeds
    ?.map((seed) => seed.bands)
    .filter((bands): bands is RankBRow => Boolean(bands));
  return histogramFromRows(seedBands);
}

export type AblationScores = {
  virality: number | null;
  relevance: number | null;
  nuance: number | null;
  contrarian: number | null;
  total: number | null;
};

export type AblationArm = {
  id: string;
  virality: number | null;
  relevance: number | null;
  nuance: number | null;
  contrarian: number | null;
  total: number | null;
  winner: boolean;
};

export type AblationTable = {
  locked: boolean;
  weights: {
    virality: number;
    relevance: number;
    nuance: number;
    contrarian: number;
  };
  arms: AblationArm[];
  winnerId: string | null;
  note: string;
};

function asScore(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
}

function scoreFromArm(arm: Record<string, unknown>, key: string): number | null {
  return asScore(arm[key]) ?? asScore(arm[`mean_${key}`]);
}

function placeholderArms(winnerId: string | null): AblationArm[] {
  return ABLATION_ARM_IDS.map((id) => ({
    id,
    virality: null,
    relevance: null,
    nuance: null,
    contrarian: null,
    total: null,
    winner: winnerId === id,
  }));
}

function firstSeedAblation(payload: {
  ablation?: { seeds?: Array<{ arms?: unknown; winner_id?: string | null; weights?: unknown }> };
}): { arms?: unknown; winner_id?: string | null; weights?: unknown } | undefined {
  return payload.ablation?.seeds?.[0];
}

export function ablationTableFrom(payload: {
  ablation?: {
    locked?: boolean;
    winner_id?: string | null;
    note?: string;
    weights?: Partial<AblationTable['weights']>;
    arms?: Array<Record<string, unknown> & { id?: string }>;
    seeds?: Array<{
      arms?: Array<Record<string, unknown> & { id?: string }>;
      winner_id?: string | null;
      weights?: Partial<AblationTable['weights']>;
      locked?: boolean;
    }>;
  };
} | null | undefined): AblationTable {
  const source = payload?.ablation;
  const nested = firstSeedAblation(payload ?? {});
  const winnerId = source?.winner_id ?? nested?.winner_id ?? null;
  const nestedWeights = (nested?.weights ?? {}) as Partial<AblationTable['weights']>;
  const weights = {
    virality: source?.weights?.virality ?? nestedWeights.virality ?? EQUAL_ABLATION_WEIGHTS.virality,
    relevance:
      source?.weights?.relevance ?? nestedWeights.relevance ?? EQUAL_ABLATION_WEIGHTS.relevance,
    nuance: source?.weights?.nuance ?? nestedWeights.nuance ?? EQUAL_ABLATION_WEIGHTS.nuance,
    contrarian:
      source?.weights?.contrarian ?? nestedWeights.contrarian ?? EQUAL_ABLATION_WEIGHTS.contrarian,
  };
  const rawArms = source?.arms?.length ? source.arms : nested?.arms;
  const arms = rawArms?.length
    ? rawArms
        .filter((arm) => typeof arm.id === 'string' && arm.id.length > 0)
        .map((arm) => ({
          id: arm.id as string,
          virality: scoreFromArm(arm, 'virality'),
          relevance: scoreFromArm(arm, 'relevance'),
          nuance: scoreFromArm(arm, 'nuance'),
          contrarian: scoreFromArm(arm, 'contrarian'),
          total: scoreFromArm(arm, 'total') ?? scoreFromArm(arm, 'weighted_total'),
          winner: Boolean(arm.winner) || arm.id === winnerId,
        }))
    : placeholderArms(winnerId);
  return {
    locked: Boolean(source?.locked ?? (nested as { locked?: boolean } | undefined)?.locked),
    weights,
    arms,
    winnerId: arms.find((arm) => arm.winner)?.id ?? winnerId,
    note:
      source?.note ??
      'Scores from today\'s pack. Weights lock after a live run. All three pairing distances stay in the grid.',
  };
}

export function rescoreAblationTable(
  table: AblationTable,
  weights: AblationWeights,
): AblationTable {
  const nextWeights: AblationWeights = {
    virality: clampWeight(weights.virality),
    relevance: clampWeight(weights.relevance),
    nuance: clampWeight(weights.nuance),
    contrarian: clampWeight(weights.contrarian),
  };
  const arms = table.arms.map((arm) => ({
    ...arm,
    total: weightedAblationTotal(arm, nextWeights),
    winner: false,
  }));
  let winnerId: string | null = null;
  let best = Number.NEGATIVE_INFINITY;
  for (const arm of arms) {
    if (arm.total == null) continue;
    if (arm.total > best) {
      best = arm.total;
      winnerId = arm.id;
    }
  }
  return {
    ...table,
    locked: false,
    weights: nextWeights,
    arms: arms.map((arm) => ({ ...arm, winner: arm.id === winnerId })),
    winnerId,
  };
}

export function seedsFromPack<T>(pack: { seeds?: T[] } | null | undefined): T[] {
  return pack?.seeds ?? [];
}

export function seedCountFromPack(pack: {
  seeds?: unknown[];
  stats?: { seed_count?: number };
} | null | undefined): number {
  const seeds = pack?.seeds?.length ?? 0;
  if (seeds > 0) return seeds;
  const stated = pack?.stats?.seed_count;
  return typeof stated === 'number' && Number.isFinite(stated) ? stated : 0;
}

export function storyEligibility(seed: { relevance_score?: number; eligibility?: number } | undefined): number {
  const value = seed?.eligibility ?? seed?.relevance_score;
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function rankedStoryLabel(input: {
  rank: number;
  eligibility: number;
  clusterLabel: string;
  title: string;
}): string {
  return `#${input.rank} · ${input.eligibility.toFixed(2)} · ${input.clusterLabel} · ${input.title}`;
}

export function sortByEligibility<T>(
  rows: T[],
  scoreOf: (row: T) => number,
): T[] {
  return [...rows].sort((left, right) => scoreOf(right) - scoreOf(left));
}

export const DEFAULT_CONSULTATION =
  'SPARC earth-observation work can verify this change with current imagery instead of a site visit.';

export type RankCHit = {
  matched: true;
  similarProblem: string;
  caseStudyTitle: string;
  slug?: string;
  url?: string;
  tech?: string;
  consultation?: string;
  source?: string;
};

export type AnalystPairingDetail = {
  label: string;
  distance: number;
  setLabel: string;
  source: string;
  product: string;
  rssTitle: string;
  vertical: string;
  caseStudyTitle: string;
  similarProblem: string;
};

export function analystPairingDetails(input: {
  rows: Array<{
    label: string;
    cosine?: number | string | null;
    band?: unknown;
    human_band?: unknown;
    source?: string;
    product?: string;
  }>;
  seed?: {
    rss_title?: string;
    vertical?: string;
    product?: string;
    capability_match?: unknown;
    match?: unknown;
    rank_c?: unknown;
  };
}): AnalystPairingDetail[] {
  const hit = rankCHitFrom(input.seed);
  return input.rows.map((row) => ({
    label: row.label,
    distance: cosineValue(row.cosine),
    setLabel: bandLabel(bandFromPairingRow(row)),
    source: typeof row.source === 'string' ? row.source.trim() : '',
    product: typeof row.product === 'string' ? row.product.trim() : asText(input.seed?.product),
    rssTitle: asText(input.seed?.rss_title),
    vertical: asText(input.seed?.vertical),
    caseStudyTitle: hit?.caseStudyTitle ?? '',
    similarProblem: hit?.similarProblem ?? '',
  }));
}

export function analystDetailIsEmpty(row: AnalystPairingDetail): boolean {
  return !row.source && !row.product && !row.caseStudyTitle && !row.similarProblem;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function techFromMatch(record: Record<string, unknown>, title: string, problem: string): string {
  const direct = asText(record.tech) || asText(record.technology) || asText(record.stack);
  if (direct) return direct;
  const haystack = `${title} ${problem}`;
  const found: string[] = [];
  if (/\bGeoAI\b/i.test(haystack)) found.push('GeoAI');
  if (/\bGIS\b/.test(haystack)) found.push('GIS');
  if (/\bMRV\b/.test(haystack)) found.push('MRV');
  if (/\bSAR\b/.test(haystack)) found.push('SAR');
  if (/restoration/i.test(haystack)) found.push('restoration planning');
  return found.join(', ');
}

function matchRecordFrom(seed: Record<string, unknown> | undefined): Record<string, unknown> | null {
  if (!seed) return null;
  const candidates = [seed.capability_match, seed.match, seed.rank_c];
  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object') {
      return candidate as Record<string, unknown>;
    }
  }
  return null;
}

export function rankCHitFrom(
  seed: { rank_c?: unknown; capability_match?: unknown; match?: unknown } | undefined,
): RankCHit | null {
  const record = matchRecordFrom(seed as Record<string, unknown> | undefined);
  if (!record) return null;
  if (record.matched === false) return null;
  const similarProblem =
    asText(record.similarProblem) || asText(record.similar_problem) || asText(record.problem);
  const caseStudyTitle =
    asText(record.caseStudyTitle) ||
    asText(record.case_study_title) ||
    asText(record.title);
  const slug = asText(record.slug) || undefined;
  const url = asText(record.url) || undefined;
  if (!similarProblem && !caseStudyTitle && !slug && !url) return null;
  const rawConsultation = asText(record.consultation);
  const consultation =
    !rawConsultation ||
    /if this is your desk|contact form|sparcglobal\.ca\/contact|talk to SPARC/i.test(rawConsultation)
      ? DEFAULT_CONSULTATION
      : rawConsultation;
  return {
    matched: true,
    similarProblem,
    caseStudyTitle,
    slug,
    url,
    tech: techFromMatch(record, caseStudyTitle, similarProblem) || undefined,
    consultation,
    source: asText(record.source) || undefined,
  };
}

export function preferLiveCapabilityHit(
  live: RankCHit | null,
  seed: { rank_c?: unknown; capability_match?: unknown; match?: unknown } | undefined,
): RankCHit | null {
  return live ?? rankCHitFrom(seed);
}

export function rankCAttachRate(keepCount: number, matchCount: number): number {
  if (keepCount <= 0) return 0;
  return matchCount / keepCount;
}

export function higgsfieldPromptFrom(input: {
  title?: string;
  dek?: string;
  vertical?: string;
}): string {
  const title = (input.title ?? '').replace(/\s+/g, ' ').trim();
  const dek = (input.dek ?? '').replace(/\s+/g, ' ').trim().slice(0, 280);
  const cluster = clusterKeyFromVertical(input.vertical);
  let subject = title || (EO_CLUSTER_IDS.has(cluster) ? 'earth observation scene' : 'documentary scene');
  if (dek) subject = title ? `${title}. ${dek}` : dek;
  if (EO_CLUSTER_IDS.has(cluster)) {
    const label = stillModalityFromStory(title, dek, cluster);
    return (
      `Photorealistic overhead earth-observation still matching this news story: ` +
      `${subject} Vertical: ${cluster || 'geoai'}. Style: illustrative ${label} ` +
      'remote-sensing photograph. ' +
      'Not a live SPARC capture of this news AOI. ' +
      `${STILL_BAN_UI} ` +
      'Natural or false-color remote-sensing base, no logos, no people, ' +
      'no infographic.'
    );
  }
  const scene = documentarySceneFromStory(title, dek, cluster);
  return (
    `Photorealistic documentary still matching this news story: ` +
    `${subject} Vertical: ${cluster || 'procurement'}. Scene: ${scene}. ` +
    'Not an overhead remote-sensing frame. Not a live SPARC capture. ' +
    `${STILL_BAN_UI} ` +
    'No SPARC product UI, no dashboards, no logos.'
  );
}

export type StillPreview = {
  prompt: string;
  caption: string;
  stillUrl?: string;
  stillExists: boolean;
};

function generatedStillUrlFrom(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.startsWith('data:image/')) return trimmed;
  if (/^https:\/\/[a-z0-9-]+\.blob\.core\.windows\.net\//i.test(trimmed)) return trimmed;
  return undefined;
}

export function stillPreviewFrom(input: {
  pack?: Record<string, unknown>;
  seed?: Record<string, unknown>;
  drafts?: { insightsTitle?: string; insightsDek?: string; beehiivHeadline?: string };
  vertical?: string;
  generatedStillUrl?: string;
}): StillPreview {
  const pack = input.pack ?? {};
  const seed = input.seed ?? {};
  const stats = (pack.stats && typeof pack.stats === 'object' ? pack.stats : {}) as Record<
    string,
    unknown
  >;
  const higgs =
    (pack.higgsfield && typeof pack.higgsfield === 'object' ? pack.higgsfield : {}) as Record<
      string,
      unknown
    >;
  const prompt =
    asText(seed.still_prompt) ||
    asText(pack.still_prompt) ||
    asText(higgs.prompt) ||
    asText(stats.still_prompt) ||
    higgsfieldPromptFrom({
      title: input.drafts?.insightsTitle || input.drafts?.beehiivHeadline || asText(seed.rss_title),
      dek: input.drafts?.insightsDek,
      vertical: input.vertical || asText(seed.vertical) || 'forest',
    });
  const stillUrl =
    generatedStillUrlFrom(input.generatedStillUrl) ||
    generatedStillUrlFrom(seed.generated_still_url) ||
    generatedStillUrlFrom(pack.generated_still_url);
  return {
    prompt,
    caption: STILL_CAPTION,
    stillUrl,
    stillExists: Boolean(stillUrl),
  };
}

export function clipForFeed(text: string, cut: number): { visible: string; truncated: boolean } {
  if (text.length <= cut) return { visible: text, truncated: false };
  return { visible: text.slice(0, cut).trimEnd(), truncated: true };
}

export function charCountLabel(length: number, max: number): string {
  return `${length} / ${max}`;
}

export type LinkedTextPart = { type: 'text'; value: string } | { type: 'link'; href: string };

const URL_RE = /https?:\/\/[^\s)]+/g;

export function splitTextWithLinks(text: string): LinkedTextPart[] {
  const parts: LinkedTextPart[] = [];
  let last = 0;
  URL_RE.lastIndex = 0;
  let match: RegExpExecArray | null = URL_RE.exec(text);
  while (match) {
    if (match.index > last) {
      parts.push({ type: 'text', value: text.slice(last, match.index) });
    }
    parts.push({ type: 'link', href: match[0] });
    last = match.index + match[0].length;
    match = URL_RE.exec(text);
  }
  if (last < text.length) {
    parts.push({ type: 'text', value: text.slice(last) });
  }
  return parts.length > 0 ? parts : [{ type: 'text', value: text }];
}

export function looksLikeHtml(text: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(text);
}

export function previewHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/javascript:/gi, '');
}

export type BeehiivStorySection = {
  storyId: string;
  title: string;
  source: string;
  bullets: string[];
  html: string;
  articleUrl: string;
  cta?: string;
  imageUrl?: string;
};

export type BeehiivLetter = {
  clusterId: string;
  label: string;
  headline: string;
  html: string;
  serverHtml?: string;
  sections: BeehiivStorySection[];
  publishedUrl?: string;
  postId?: string;
  scheduledAt?: string;
};

export type InsightsPost = {
  clusterId: string;
  label: string;
  title: string;
  dek: string;
  body: string;
  storyId: string;
  publishedUrl?: string;
};

export type OrganicDraftState = {
  beehiivHeadline: string;
  beehiivHtml: string;
  beehiivDetails: string;
  beehiivWhy: string;
  beehiivLetters?: BeehiivLetter[];
  insightsPosts?: InsightsPost[];
  insightsTitle: string;
  insightsDek: string;
  insightsBody: string;
  linkedinExcerpt: string;
  linkedinUrl: string;
  xText: string;
  linkedinPosts?: Array<{ storyId: string; label: string; storyTitle?: string; excerpt: string; url: string }>;
  xPosts?: Array<{ storyId: string; label: string; storyTitle?: string; text: string }>;
};

export function emptyOrganicDrafts(): OrganicDraftState {
  return {
    beehiivHeadline: '',
    beehiivHtml: '',
    beehiivDetails: '',
    beehiivWhy: '',
    beehiivLetters: [],
    insightsPosts: [],
    insightsTitle: '',
    insightsDek: '',
    insightsBody: '',
    linkedinExcerpt: '',
    linkedinUrl: '',
    xText: '',
    linkedinPosts: [],
    xPosts: [],
  };
}

export type OrganicDraftArtifact = {
  date: string;
  seedId: string;
  bangerId: string;
  drafts: OrganicDraftState;
  savedAt: string;
  sent: false;
  sendBeehiiv: false;
};

export function draftStorageKey(packDate: string, seedId: string, bangerId: string): string {
  return `${ORGANIC_DRAFT_STORAGE_PREFIX}:${packDate}:${seedId}:${bangerId}`;
}

function isDraftState(value: unknown): value is OrganicDraftState {
  if (!value || typeof value !== 'object') return false;
  const record = value as OrganicDraftState;
  return typeof record.beehiivHeadline === 'string' && typeof record.insightsTitle === 'string';
}

export function loadOrganicDraft(
  packDate: string,
  seedId: string,
  bangerId: string,
): OrganicDraftState | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(draftStorageKey(packDate, seedId, bangerId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const record = parsed as { drafts?: unknown };
    return isDraftState(record.drafts) ? record.drafts : isDraftState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveOrganicDraft(input: {
  date: string;
  seedId: string;
  bangerId: string;
  drafts: OrganicDraftState;
}): OrganicDraftArtifact {
  const artifact: OrganicDraftArtifact = {
    date: input.date,
    seedId: input.seedId,
    bangerId: input.bangerId,
    drafts: input.drafts,
    savedAt: new Date().toISOString(),
    sent: false,
    sendBeehiiv: false,
  };
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(
      draftStorageKey(input.date, input.seedId, input.bangerId),
      JSON.stringify(artifact),
    );
  }
  return artifact;
}

export function downloadOrganicArtifact(artifact: OrganicDraftArtifact): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([JSON.stringify(artifact, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `organic-draft-${artifact.date}-${artifact.seedId}-${artifact.bangerId}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export function channelLabel(channel: Channel): string {
  switch (channel) {
    case 'beehiiv':
      return 'Beehiiv';
    case 'insights':
      return 'Insights';
    case 'linkedin':
      return 'LinkedIn';
    case 'x':
      return 'X';
    default: {
      const _exhaustive: never = channel;
      return _exhaustive;
    }
  }
}

export function previewSurfaceLabel(surface: PreviewSurface): string {
  switch (surface) {
    case 'still':
      return 'Still prompt';
    case 'beehiiv':
    case 'insights':
    case 'linkedin':
    case 'x':
      return channelLabel(surface);
    default: {
      const _exhaustive: never = surface;
      return _exhaustive;
    }
  }
}

export function detailsList(details: string): string[] {
  return details
    .split(/\n+/)
    .map((line) => line.replace(/^[-*]\s+/, '').trim())
    .filter((line) => line.length > 0);
}
